import pdfMake from 'pdfmake/build/pdfmake';
import Logo from 'Icons/RMlogo.png';
import { TreeService } from 'business/services/TreeService';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import { getParentPath } from 'utils/Global';
import {
  createPdfWithPopupBlockerHandler,
  estimateRunsLineCount,
  getDesignHeaderRuns,
  getScreenNameLineRuns,
  BASE_PAGE_MARGINS,
  HEADER_ROW_MARGIN,
  HEADER_FONT_SIZE,
  HEADER_LINE_HEIGHT,
  MAX_TOP_MARGIN,
  HEADER_MAX_LINE_CHARS,
  PDFTextProps,
} from './utils';
import { pageSize, fonts } from './ReportsConstants';
pdfMake.fonts = { Roboto: fonts.Roboto };

type PageMargins = {
  left: number;
  top: number;
  right: number;
  bottom: number;
};

export async function createCanvasDesignPDFReport(
  imageUri: string,
  plateId: number,
  plateBar: string,
  expId: number,
  expName: string,
  saveReport: boolean,
  isMultiPlateExperiment: boolean,
  screenNames: string[],
  onPopupBlocked?: () => void,
) {
  const headerRuns = await getHeaderRuns(plateId, plateBar, expId, screenNames);
  const pageMargins = getPageMargins(headerRuns);
  const pdfDefinition = {
    info: {
      title: expName + ' - Canvas Design Report',
      author: 'Rock Maker',
      subject: 'Experiment Design',
    },
    content: CreateContent(imageUri, isMultiPlateExperiment, pageMargins),
    styles: {
      headerText: { fontSize: HEADER_FONT_SIZE },
      tableHeader: {
        lineHeight: 1,
        bold: true,
        fontSize: '12',
        alignment: 'center',
        fillColor: '#D9D9D9',
      },
      tableHeaderWithoutMargin: {
        bold: true,
        alignment: 'center',
        margin: [2, 2, 2, 2],
        fillColor: '#cccccc',
      },
      tableSubHeader: { bold: true, fillColor: '#cccccc' },
      termList: { margin: [0, 7, 0, 0] },
    },
    defaultStyle: {
      fontSize: 10,
      lineHeight: 1.5,
    },
    pageMargins: pageMargins,
    header: getHeaderContent(headerRuns),
    pageBreakBefore: false,
    pageOrientation: 'landscape',
    pageSize: 'A4',
  };

  createPdfWithPopupBlockerHandler(
    pdfMake,
    pdfDefinition,
    expName + ' - Canvas Design Report',
    saveReport,
    onPopupBlocked,
  );
}

function CreateContent(uri, isMultiPlateExperiment, pageMargins: PageMargins) {
  const verticalMargins = pageMargins.top + pageMargins.bottom;
  const availableWidth = pageSize.a4.height;
  const availableHeight = pageSize.a4.width - verticalMargins;

  return {
    image: uri,
    fit: [availableWidth, availableHeight],
    alignment: 'center',
  };
}

async function getHeaderRuns(plateId, plateBar, expId, screenNames: string[]) {
  const treeService = new TreeService();
  const expParents = await treeService.getDocumentParentNodes(
    expId,
    ETreeNodeTypes.Experiment,
    true,
  );
  const expPath = expParents ? getParentPath(expParents) : '';
  const barCode = plateBar.split(',')[2];
  const screenNameRuns = getScreenNameLineRuns(screenNames, HEADER_MAX_LINE_CHARS);

  return getDesignHeaderRuns(expPath, plateId, barCode, screenNameRuns);
}

const getPageMargins = (headerRuns: PDFTextProps[]): PageMargins => {
  const lineCount = estimateRunsLineCount(headerRuns, HEADER_MAX_LINE_CHARS);
  const headerContentHeight = lineCount * HEADER_FONT_SIZE * HEADER_LINE_HEIGHT;
  const requiredTopMargin =
    HEADER_ROW_MARGIN[1] + headerContentHeight + HEADER_ROW_MARGIN[3];
  const topMargin = Math.min(
    Math.max(BASE_PAGE_MARGINS[1], requiredTopMargin),
    MAX_TOP_MARGIN,
  );

  return {
    left: BASE_PAGE_MARGINS[0],
    top: topMargin,
    right: BASE_PAGE_MARGINS[2],
    bottom: BASE_PAGE_MARGINS[3],
  };
};

function getHeaderContent(headerRuns: PDFTextProps[]) {
  return [
    {
      columns: [
        {
          image: Logo,
          width: 20,
          height: 20,
        },
        {
          text: 'ROCK MAKER®',
          margin: [5, 0, 0, 0],
          width: 100,
        },
        {
          text: headerRuns,
          style: 'headerText',
        },
      ],
      margin: HEADER_ROW_MARGIN,
    },
  ];
}
